// Sprint 11 UX.S1 — Visibility helpers for the dashboard "Generate UX Specs"
// action. The button targets projects that are past Phase 2.5 (Phase 3 and
// later) and adapts its label / behaviour when a user_journey_map already
// exists for the project.

const PHASES_PAST_UX_DESIGN = new Set<string>([
  'phase_3_sprint_planning',
  'phase_4_development',
  'phase_5_code_review',
  'phase_6_testing',
  'phase_7_deployment',
  'phase_8_retrospective',
]);

/**
 * Returns true when the project's current phase is past Phase 2.5 — the only
 * place the standalone "Generate UX Specs" button is rendered. Earlier phases
 * use the gated flow (Phase 1/2 → Phase 2.5 handoff) and do not need a
 * standalone trigger.
 */
export function isProjectPastUxDesign(currentPhase: string | null | undefined): boolean {
  if (!currentPhase) return false;
  return PHASES_PAST_UX_DESIGN.has(currentPhase);
}

/**
 * Returns true when the supplied artefact list already contains a UX
 * deliverable. Used to switch the dashboard CTA between "Generate UX Specs"
 * and "Regenerate UX Specs" and to surface the already-exists confirmation.
 */
export function hasUxArtefacts(artefacts: Array<{ type: string }>): boolean {
  return artefacts.some(a => a.type === 'user_journey_map');
}

/**
 * Decides whether ChatPage should render a session loaded by `?sessionId=…`
 * in read-only mode. The same URL is used by two flows:
 *   • the dashboard "Generate UX Specs" action (standalone UX.S1) — a
 *     freshly-created active session that must accept input;
 *   • the chat header "Session History" dropdown — picks an old completed
 *     session for review.
 *
 * The session row's status is the source of truth. Sessions without an
 * explicit status (legacy rows or partial fixtures) default to read-only —
 * the safer fallback because it preserves the long-standing behaviour
 * before UX.S1 introduced the "open active session by ID" path.
 */
export function isReadOnlySession(status: string | null | undefined): boolean {
  return status !== 'active';
}
